import React, { useEffect, useState } from 'react';
import {
  hämtaDubbletter, slåIhopKunder, markeraInteSamma, DubblettPar, DubblettKund,
} from '../api/client';
import { TEMA } from '../theme';

const monoStyle: React.CSSProperties = { fontFamily: "'IBM Plex Mono', monospace" };

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '18px 22px',
  marginBottom: '12px',
};

const sekundärKnapp: React.CSSProperties = {
  height: '36px',
  padding: '0 18px',
  borderRadius: '2px',
  background: 'transparent',
  border: `1px solid ${TEMA.border}`,
  color: TEMA.inkSoft,
  fontFamily: "'Inter', sans-serif",
  fontSize: '13.5px',
  cursor: 'pointer',
};

function historik(k: DubblettKund): number {
  return k.inkopare + k.moten + k.paminnelser;
}

// Kunden med mest historik behålls, eftersom det är den säljarna arbetat med
// och vars namn de känner igen. Vid lika väljs det kortare namnet:
// "Bambi" snarare än "BAMBI i Stockholm AB".
function förvaltBehåll(p: DubblettPar): string {
  const ha = historik(p.a), hb = historik(p.b);
  if (ha !== hb) return ha > hb ? p.a.kund_id : p.b.kund_id;
  return p.a.namn.length <= p.b.namn.length ? p.a.kund_id : p.b.kund_id;
}

function nyckel(p: DubblettPar): string {
  return `${p.a.kund_id}:${p.b.kund_id}`;
}

function antal(n: number, en: string, flera: string): string {
  return `${n} ${n === 1 ? en : flera}`;
}

// Det den behållna kunden tar över från den andra. Motsvarar vad
// sla_ihop_kunder gör: tomma fält fylls, historiken flyttas.
function vadFår(kvar: DubblettKund, bort: DubblettKund): string[] {
  const delar: string[] = [];
  if (!kvar.fortnox_kund_nr && bort.fortnox_kund_nr) delar.push(`#${bort.fortnox_kund_nr}`);
  if (!kvar.ansvarig_säljare && bort.ansvarig_säljare) delar.push(bort.ansvarig_säljare);
  if (!kvar.stad && bort.stad) delar.push(bort.stad);
  if (bort.inkopare) delar.push(antal(bort.inkopare, 'inköpare', 'inköpare'));
  if (bort.moten) delar.push(antal(bort.moten, 'möte', 'möten'));
  if (bort.paminnelser) delar.push(antal(bort.paminnelser, 'påminnelse', 'påminnelser'));
  return delar;
}

function KundRuta({ k, andra, behålls, onVälj, disabled }: {
  k: DubblettKund; andra: DubblettKund; behålls: boolean; onVälj: () => void; disabled: boolean;
}) {
  const får = behålls ? vadFår(k, andra) : [];
  return (
    <button
      type="button"
      onClick={onVälj}
      disabled={disabled}
      style={{
        flex: '1 1 260px', textAlign: 'left', padding: '14px 16px',
        borderRadius: '4px', cursor: disabled ? 'default' : 'pointer',
        background: behålls ? TEMA.bg : 'transparent',
        border: `1px solid ${behålls ? TEMA.accent : TEMA.border}`,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'baseline' }}>
        <span style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink }}>{k.namn}</span>
        <span style={{ fontSize: '11.5px', color: behålls ? TEMA.accent : TEMA.inkFaint, whiteSpace: 'nowrap' }}>
          {behålls ? 'Namnet behålls' : 'Går upp i den andra'}
        </span>
      </div>
      <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: TEMA.inkSoft }}>
        {k.stad && <>{k.stad} · </>}
        {k.fortnox_kund_nr
          ? <span style={monoStyle}>#{k.fortnox_kund_nr}</span>
          : <span style={{ color: TEMA.inkFaint }}>Inte i Fortnox</span>}
        {' · '}
        {k.ansvarig_säljare || <span style={{ color: TEMA.inkFaint }}>Ej tilldelad</span>}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: historik(k) ? TEMA.inkSoft : TEMA.inkFaint }}>
        {historik(k) === 0
          ? 'Ingen historik'
          : [
              k.inkopare && antal(k.inkopare, 'inköpare', 'inköpare'),
              k.moten && antal(k.moten, 'möte', 'möten'),
              k.paminnelser && antal(k.paminnelser, 'påminnelse', 'påminnelser'),
            ].filter(Boolean).join(' · ')}
      </p>
      {får.length > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: TEMA.accent }}>
          Får {får.join(', ')}
        </p>
      )}
    </button>
  );
}

export default function Dubbletter() {
  const [par, setPar] = useState<DubblettPar[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const [arbetar, setArbetar] = useState<string | null>(null);
  const [val, setVal] = useState<Record<string, string>>({});
  const [besked, setBesked] = useState<string | null>(null);

  async function ladda() {
    const r = await hämtaDubbletter();
    setPar(r.data);
    setVal(Object.fromEntries(r.data.map(p => [nyckel(p), förvaltBehåll(p)])));
  }

  useEffect(() => {
    ladda()
      .catch(err => {
        console.error('Kunde inte ladda dubbletterna:', err);
        setFel('Kunde inte ladda listan just nu. Försök igen om en stund.');
      })
      .finally(() => setLaddar(false));
  }, []);

  async function slåIhop(p: DubblettPar) {
    const behåll = val[nyckel(p)] ?? förvaltBehåll(p);
    const [kvar, bort] = behåll === p.a.kund_id ? [p.a, p.b] : [p.b, p.a];
    const ok = window.confirm(
      `"${bort.namn}" går upp i "${kvar.namn}", som behåller sitt namn` +
      (vadFår(kvar, bort).length ? ` och får ${vadFår(kvar, bort).join(', ')}` : '') +
      `.\n\nDet går inte att ångra.`,
    );
    if (!ok) return;
    setArbetar(nyckel(p));
    try {
      const { data: r } = await slåIhopKunder(kvar.kund_id, bort.kund_id);
      const flyttat = [
        r.inkopare_flyttade && antal(r.inkopare_flyttade, 'inköpare', 'inköpare'),
        r.inkopare_hopslagna && `${antal(r.inkopare_hopslagna, 'inköpare', 'inköpare')} sammanslagen med befintlig`,
        r.moten && antal(r.moten, 'möte', 'möten'),
        r.paminnelser && antal(r.paminnelser, 'påminnelse', 'påminnelser'),
      ].filter(Boolean);
      setBesked(
        `${bort.namn} är sammanslagen med ${kvar.namn}.` +
        (flyttat.length ? ` Flyttat: ${flyttat.join(', ')}.` : ''),
      );
      // Hämtas om: andra par med den borttagna kunden finns inte längre.
      await ladda();
      window.dispatchEvent(new Event('vinsales:dubbletter-andrade'));
    } catch (err: any) {
      console.error('Kunde inte slå ihop:', err);
      alert(err?.response?.data?.detail || 'Kunde inte slå ihop. Försök igen.');
    } finally {
      setArbetar(null);
    }
  }

  async function inteSamma(p: DubblettPar) {
    setArbetar(nyckel(p));
    try {
      await markeraInteSamma(p.a.kund_id, p.b.kund_id);
      setPar(prev => prev.filter(x => nyckel(x) !== nyckel(p)));
      window.dispatchEvent(new Event('vinsales:dubbletter-andrade'));
    } catch (err) {
      console.error('Kunde inte spara:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetar(null);
    }
  }

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
    </div>
  );

  if (fel) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  function visaPar(p: DubblettPar) {
    const n = nyckel(p);
    const upptagen = arbetar === n;
    const behåll = val[n] ?? förvaltBehåll(p);
    const välj = (id: string) => setVal(prev => ({ ...prev, [n]: id }));
    return (
      <div key={n} style={kortStyle}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <KundRuta k={p.a} andra={p.b} behålls={behåll === p.a.kund_id} onVälj={() => välj(p.a.kund_id)} disabled={upptagen} />
          <KundRuta k={p.b} andra={p.a} behålls={behåll === p.b.kund_id} onVälj={() => välj(p.b.kund_id)} disabled={upptagen} />
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => slåIhop(p)}
            disabled={upptagen}
            style={{
              height: '36px', padding: '0 18px', borderRadius: '2px',
              background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
              fontFamily: "'Inter', sans-serif", fontSize: '13.5px', fontWeight: 500,
              cursor: upptagen ? 'default' : 'pointer', opacity: upptagen ? 0.6 : 1,
            }}
          >
            {upptagen ? 'Sparar...' : 'Slå ihop'}
          </button>
          <button onClick={() => inteSamma(p)} disabled={upptagen} style={sekundärKnapp}>
            Inte samma
          </button>
          <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint, marginLeft: 'auto' }}>
            likhet {p.likhet.toFixed(2)}
          </span>
        </div>
      </div>
    );
  }

  const sammaOrt = par.filter(p => !p.olika_ort);
  const olikaOrt = par.filter(p => p.olika_ort);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>Möjliga dubbletter</h1>
        <p style={{ margin: '10px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '680px', lineHeight: 1.5 }}>
          Kunder som troligen är samma ställe, oftast ett prospect från importen och samma kund
          från Fortnox. Klicka på den som ska behållas: dess namn blir kvar, och inköpare, möten och
          påminnelser flyttas dit, liksom Fortnox-nummer och ansvarig säljare om den saknar dem. Två kunder med olika Fortnox-nummer visas aldrig här.
        </p>
      </div>

      {besked && (
        <div style={{ ...kortStyle, display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '13.5px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{besked}</span>
          <button onClick={() => setBesked(null)} style={{ ...sekundärKnapp, height: '28px', padding: '0 12px' }}>Stäng</button>
        </div>
      )}

      {par.length === 0 && (
        <div style={{ ...kortStyle, textAlign: 'center', padding: '40px 22px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
            Inga möjliga dubbletter just nu.
          </p>
        </div>
      )}

      {sammaOrt.map(visaPar)}

      {olikaOrt.length > 0 && (
        <div style={{ margin: sammaOrt.length ? '36px 0 16px' : '0 0 16px' }}>
          <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '20px', color: TEMA.ink }}>Samma namn, olika ort</h2>
          <p style={{ margin: '8px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '680px', lineHeight: 1.5 }}>
            Korten heter nästan likadant men står på olika orter. Ofta är det samma ställe, där Fortnox har
            bolagets postort och säljaren har skrivit orten där stället ligger. Det kan också vara två olika
            ställen med samma namn. Välj Inte samma för dem, så visas paret inte igen.
          </p>
        </div>
      )}

      {olikaOrt.map(visaPar)}
    </div>
  );
}
